// 📁 src/components/BotSettings/BotMenu.tsx
// src/components/BotSettings/BotMenu.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { Button } from '../../ui/Button';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { runBacktest } from '@/features/backtests/runBacktest';
import { toStrategyKey } from '@/lib/strategyKeys';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import BacktestPeriodModal from './BacktestPeriodModal';
import menuConfig from './menu.config';

export type SectionId =
  | 'basic' | 'exchanges' | 'strategy' | 'coins' | 'amounts'
  | 'primary-time' | 'secondary-time' | 'parameters' | 'auto-close'
  | 'buy' | 'buy-take-profit' | 'buy-trailing-entry' | 'buy-take-profit-stages'
  | 'sell-strategy' | 'sell-take-profit' | 'sell-trailing-stop' | 'sell-take-profit-stages'
  | 'notifications';

interface MenuItem { id: string; label: string; icon: string; children?: MenuItem[]; }
const MENU_ITEMS: MenuItem[] = menuConfig.MENU_ITEMS;

interface BotMenuProps {
  activeSection: SectionId;
  onSectionChange: (section: SectionId) => void;
  isCollapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  isMainSidebarCollapsed?: boolean;
  form: any;
  isSaving: boolean;
  onSave?: (options?: { redirect?: boolean; toast?: boolean; silent?: boolean }) => Promise<void>;
  longEnabled?: boolean;
  shortEnabled?: boolean;
}

const BotMenu: React.FC<BotMenuProps> = ({
  activeSection,
  onSectionChange,
  isCollapsed,
  onCollapse,
  isMainSidebarCollapsed = false,
  form,
  isSaving,
  onSave,
  longEnabled = true,
  shortEnabled = true
}) => {
  const navigate = useNavigate();
  const [isBacktestModalOpen, setIsBacktestModalOpen] = useState(false);
  const [isRunningBacktest, setIsRunningBacktest] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const newExpanded: Record<string, boolean> = {};
    for (const item of MENU_ITEMS) {
      if (item.children) {
        const hasActiveChild = item.children.some(child => child.id === activeSection);
        newExpanded[item.id] = hasActiveChild;
      }
    }
    setExpandedGroups(newExpanded);
  }, [activeSection]);

  const handleSave = useCallback((e?: React.MouseEvent) => {
    if (e && e.preventDefault) e.preventDefault();
    if (window.handleSave) {
      window.handleSave();
    } else {
      toast.error('Save handler not available');
    }
  }, []);

  const handleBacktest = useCallback(() => { setIsBacktestModalOpen(true); }, []);

  const validateBotForRun = useCallback((formData: any) => {
    const errors: Array<{ section: SectionId; msg: string }> = [];
    const signalOnly = Boolean(formData?.signalOnlyMode);

    if (!formData?.name?.trim()) errors.push({ section: 'basic', msg: 'Bot name is required.' });
    if (!signalOnly && !toStrategyKey(formData?.strategy)) errors.push({ section: 'strategy', msg: 'Valid strategy required.' });
    if (signalOnly && (!Array.isArray(formData?.primaryTimeIndicators) || !formData.primaryTimeIndicators.length)) errors.push({ section: 'primary-time', msg: 'Signal Only requires indicators.' });
    if (!Array.isArray(formData?.selectedCoins) || !formData.selectedCoins.length) errors.push({ section: 'coins', msg: 'At least one coin required.' });

    return { ok: errors.length === 0, errors };
  }, []);

  const handleBacktestConfirm = useCallback(async (days: number, customRange?: { from: string; to: string }) => {
    if (!form?.id) return toast.error('Please save the bot first');

    const { ok, errors } = validateBotForRun(form);
    if (!ok) { toast.error(errors[0].msg); onSectionChange(errors[0].section); return; }

    if (isRunningBacktest) return;
    setIsRunningBacktest(true);

    try {
      if (onSave) await onSave({ redirect: false, toast: false, silent: true });
      else if (window.handleSave) await window.handleSave({ redirect: false, toast: false, silent: true });

      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return toast.error('Not authenticated');

      let toDate: Date, fromDate: Date;
      if (customRange) {
        toDate = new Date(customRange.to);
        fromDate = new Date(customRange.from);
      } else {
        const today = new Date();
        toDate = new Date(today);
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - days);
      }

      const result = await runBacktest({
        userId: user.id,
        botId: form.id,
        fromDate,
        toDate,
        quoteCurrency: form.quoteCurrency,
        title: (form.displayTitle || form.name || '').trim() || undefined,
        form: form
      });

      if (result.warning) toast.warning(result.warning);
      else if (result.action === 'updated') toast.success(`Backtest results updated`);
      else toast.success(`New backtest created`);

      setIsBacktestModalOpen(false);
      navigate('/backtests');
    } catch (e: any) {
      toast.error(`Backtest failed: ${e.message || 'Unknown error'}`);
      setIsBacktestModalOpen(false);
    } finally {
      setIsRunningBacktest(false);
    }
  }, [form, isRunningBacktest, navigate, onSave, onSectionChange, validateBotForRun]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  }, []);

  const LONG_CHILD_IDS = ['buy-take-profit', 'buy-trailing-entry', 'buy-take-profit-stages'];
  const SHORT_CHILD_IDS = ['sell-take-profit', 'sell-trailing-stop', 'sell-take-profit-stages'];

  const isChildDisabled = useCallback((childId: string, parentId: string): boolean => {
    if (parentId === 'long-settings' && LONG_CHILD_IDS.includes(childId)) return !longEnabled;
    if (parentId === 'short-settings' && SHORT_CHILD_IDS.includes(childId)) return !shortEnabled;
    return false;
  }, [longEnabled, shortEnabled]);

  const isGroupOff = useCallback((groupId: string): boolean => {
    if (groupId === 'long-settings') return !longEnabled;
    if (groupId === 'short-settings') return !shortEnabled;
    return false;
  }, [longEnabled, shortEnabled]);

  const renderMenuItem = useCallback((item: MenuItem) => {
    if (item.children) {
      const isExpanded = expandedGroups[item.id] || false;
      const isOff = isGroupOff(item.id);

      return (
        <div key={item.id}>
          <button onClick={() => toggleGroup(item.id)} className={cn("w-full flex items-center justify-between px-4 py-2 text-sm font-medium rounded-lg transition-colors", isOff ? "text-gray-500 bg-gray-50/50" : "text-gray-700 hover:bg-gray-50", isCollapsed ? "justify-center" : "")}>
            {!isCollapsed && (
              <>
                <div className="flex items-center">
                  {item.icon && <span className={cn("text-lg mr-3", isOff && "opacity-50")}>{item.icon}</span>}
                  <span className={cn(isOff && "opacity-80")}>{item.label}</span>
                  {isOff && <span className="ml-2 text-xs text-red-500 font-normal">(OFF)</span>}
                </div>
                {isExpanded ? <ChevronUp className={cn("h-4 w-4", isOff && "opacity-50")} /> : <ChevronDown className={cn("h-4 w-4", isOff && "opacity-50")} />}
              </>
            )}
            {isCollapsed && item.icon && <span className={cn("text-lg", isOff && "opacity-50")}>{item.icon}</span>}
          </button>

          {!isCollapsed && isExpanded && (
            <div className="ml-6 mt-1 space-y-1">
              {item.children.map((child) => {
                const childDisabled = isChildDisabled(child.id, item.id);
                return (
                  <button key={child.id} onClick={() => { if (!childDisabled) onSectionChange(child.id as SectionId); }} disabled={childDisabled} className={cn("w-full flex items-center text-left px-4 py-2 text-sm rounded-lg transition-all", childDisabled ? "bg-red-50 text-red-400 cursor-not-allowed border border-red-100" : activeSection === child.id ? "bg-blue-50 text-blue-600" : "text-gray-600 hover:bg-gray-50")}>
                    {child.icon && <span className={cn("text-base mr-2", childDisabled && "opacity-50")}>{child.icon}</span>}
                    <span className={cn(childDisabled && "line-through")}>{child.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <button key={item.id} onClick={() => onSectionChange(item.id as SectionId)} className={cn("w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg", activeSection === item.id ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-50", isCollapsed ? "justify-center" : "")}>
          {item.icon && <span className={cn("text-lg", isCollapsed ? "" : "mr-3")}>{item.icon}</span>}
          {!isCollapsed && <span>{item.label}</span>}
        </button>
      );
    }
  }, [activeSection, expandedGroups, isCollapsed, onSectionChange, toggleGroup, isChildDisabled, isGroupOff]);

  return (
    <>
      <div className={cn("h-[calc(100vh-4rem)] fixed top-16 transition-all duration-300 flex flex-col z-40 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700", isCollapsed ? "w-20" : "w-64", isMainSidebarCollapsed ? "left-20" : "left-64")} style={{ zIndex: 35 }}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className={`px-4 py-2 ${isCollapsed ? 'text-center' : 'flex items-center justify-between'} mb-4`}>
            {!isCollapsed && <span className="text-sm font-medium text-gray-500">BOT SETTINGS</span>}
            <button onClick={() => onCollapse(!isCollapsed)} className={`flex items-center justify-center h-6 w-6 rounded-md hover:bg-gray-100 ${isCollapsed ? 'mx-auto' : ''}`}>
              {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-500" /> : <ChevronLeft className="h-4 w-4 text-gray-500" />}
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <nav className="space-y-1">{MENU_ITEMS.map((item) => renderMenuItem(item))}</nav>
        </div>

        <div className={cn("p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2")}>
          <Button variant="primary" size={isCollapsed ? "sm" : "md"} className="w-full" disabled={isSaving} isLoading={isSaving} onClick={handleSave} icon={<span className={cn("text-lg", isCollapsed ? "" : "mr-2")}>💾</span>}>
            {!isCollapsed && (isSaving ? "Saving..." : "Save Bot")}
          </Button>
          <Button variant="outline" size={isCollapsed ? "sm" : "md"} className="w-full" onClick={handleBacktest} disabled={!form?.selectedCoins || form.selectedCoins.length === 0} icon={<span className={cn("text-lg", isCollapsed ? "" : "mr-2")}>▶️</span>}>
            {!isCollapsed && "Run Backtest"}
          </Button>
        </div>
      </div>

      <BacktestPeriodModal isOpen={isBacktestModalOpen} onClose={() => setIsBacktestModalOpen(false)} onConfirm={handleBacktestConfirm} onNavigateToSection={onSectionChange} />
    </>
  );
};
export default BotMenu;